import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { Eye, Plus, RefreshCw, AlertCircle } from "lucide-react";
import { StatusPill, Modal } from "@/components/phase4/Phase4Common";

const REASONS = ["DAMAGED", "LOST", "PARTIAL_LOSS", "WRONG_DELIVERY", "THEFT"];

export default function CustomerInsuranceClaims() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/v2/insurance/claims", { params: { page_size: 200 } });
      setItems(r.data?.items || []);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="customer-insurance-claims">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">My Insurance Claims</h1>
            <p className="text-slate-500 text-sm mt-0.5">Track damage, loss and other insurance claims</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="px-3 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded inline-flex items-center gap-1">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={() => setRaiseOpen(true)}
              data-testid="customer-raise-claim-btn"
              className="px-3 py-1.5 bg-brand-500 hover:bg-brand-700 text-white text-xs font-semibold rounded inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Raise Claim
            </button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="customer-claims-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Claim ID", "AWB", "Reason", "Claimed ₹", "Approved ₹", "Status", "Actions"].map((h) =>
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-bold text-slate-500 uppercase">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? <tr><td colSpan={7} className="text-center py-8 text-slate-400">Loading…</td></tr>
                : items.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-slate-400">No claims raised yet.</td></tr>
                : items.map((c) => (
                  <tr key={c.claim_id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs">{c.claim_id}</td>
                    <td className="px-3 py-2 font-mono text-xs">{c.awb || "—"}</td>
                    <td className="px-3 py-2 text-xs">{(c.claim_reason || "").replace(/_/g, " ")}</td>
                    <td className="px-3 py-2 text-xs">₹{(c.claimed_amount || 0).toFixed(0)}</td>
                    <td className="px-3 py-2 text-xs">₹{(c.approved_amount || 0).toFixed(0)}</td>
                    <td className="px-3 py-2"><StatusPill status={c.status} /></td>
                    <td className="px-3 py-2">
                      <button onClick={() => setSelected(c)} className="text-brand-500 hover:text-brand-700">
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {raiseOpen && (
        <RaiseClaimModal onClose={() => setRaiseOpen(false)} onCreated={() => { setRaiseOpen(false); load(); }} />
      )}
      {selected && (
        <Modal open onClose={() => setSelected(null)} title={`Claim ${selected.claim_id}`} testId="customer-claim-detail" width="max-w-lg">
          <div className="space-y-2 text-sm">
            <StatusPill status={selected.status} />
            <Row k="AWB" v={<span className="font-mono">{selected.awb}</span>} />
            <Row k="Reason" v={(selected.claim_reason || "").replace(/_/g, " ")} />
            <Row k="Insurance Type" v={selected.insurance_type} />
            <Row k="Declared" v={`₹${(selected.declared_value || 0).toFixed(2)}`} />
            <Row k="Claimed" v={`₹${(selected.claimed_amount || 0).toFixed(2)}`} />
            <Row k="Max Payout" v={`₹${(selected.max_payout || 0).toFixed(2)}`} />
            <Row k="Approved" v={`₹${(selected.approved_amount || 0).toFixed(2)}`} />
            <div className="bg-slate-50 rounded p-2 text-xs whitespace-pre-wrap">{selected.claim_description}</div>
            {selected.rejection_reason && (
              <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs">
                <span className="font-semibold text-rose-700">Rejection:</span> {selected.rejection_reason}
              </div>
            )}
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}

function RaiseClaimModal({ onClose, onCreated }) {
  const [shipQuery, setShipQuery] = useState("");
  const [shipment, setShipment] = useState(null);
  const [reason, setReason] = useState("");
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const findShipment = async () => {
    if (!shipQuery.trim()) return;
    setErr("");
    try {
      const r = await api.get(`/v2/tracking/${encodeURIComponent(shipQuery.trim())}`);
      if (r.data?.shipment_id) {
        setShipment({ id: r.data.shipment_id, awb: r.data.awb || shipQuery.trim() });
      }
    } catch { toast.error("Shipment not found"); }
  };

  const submit = async () => {
    if (!shipment || !reason || !desc.trim() || !amount) { setErr("Fill all required fields"); return; }
    setBusy(true);
    try {
      await api.post("/v2/insurance/claims", {
        shipment_id: shipment.id,
        claim_reason: reason,
        claim_description: desc,
        claimed_amount: parseFloat(amount),
      });
      toast.success("Claim raised");
      onCreated();
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed";
      setErr(detail);
      toast.error(detail);
    }
    setBusy(false);
  };

  return (
    <Modal open onClose={onClose} title="Raise Insurance Claim" testId="customer-raise-claim-modal">
      <div className="space-y-3 text-sm">
        {err && (
          <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs flex gap-2" data-testid="claim-form-error">
            <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
            <span>{err}</span>
          </div>
        )}
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Shipment AWB *</label>
          <div className="flex gap-2">
            <input value={shipQuery} onChange={(e) => setShipQuery(e.target.value)}
              data-testid="claim-ship-search"
              className="flex-1 text-sm border border-slate-200 rounded px-2 py-1.5" />
            <button onClick={findShipment} className="px-3 py-1.5 bg-slate-100 text-xs font-semibold rounded">Find</button>
          </div>
          {shipment && <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded p-2 text-xs">✓ {shipment.awb}</div>}
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Claim Reason *</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="claim-reason"
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5">
            <option value="">— Select —</option>
            {REASONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Description *</label>
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4}
            data-testid="claim-desc"
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Claimed Amount (₹) *</label>
          <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
            data-testid="claim-amount"
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-1.5 border border-slate-200 text-xs rounded">Cancel</button>
        <button onClick={submit} disabled={busy}
          data-testid="claim-submit"
          className="px-4 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded disabled:opacity-50">
          Raise Claim
        </button>
      </div>
    </Modal>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-800">{v}</span>
    </div>
  );
}
